import { useCallback, useMemo, useState } from 'react';
import { ActionIcon, Badge, Group, Loader, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { Icon } from '@/components';
import { useEvaSyncRuns } from '@/hooks/organization-programs';
import { churchesService } from '@/services/churches.service';
import {
  EVA_SYNC_BULLETIN_MODE_LABELS,
  EVA_SYNC_RUN_STATUS,
  EVA_SYNC_RUN_STATUS_LABELS,
  type EvaSyncRunListing,
} from '@/types';
import styles from './SyncRunsTable.module.sass';

const LAYOUT = {
  SKELETON_ROWS: 3,
  SKELETON_HEIGHT: 30,
  ACTION_ICON_SIZE: 20,
};

const EVA_SYNC_RUN_STATUS_COLORS: Record<EVA_SYNC_RUN_STATUS, string> = {
  [EVA_SYNC_RUN_STATUS.PENDING]: 'yellow',
  [EVA_SYNC_RUN_STATUS.PROCESSING]: 'blue',
  [EVA_SYNC_RUN_STATUS.COMPLETED]: 'green',
  [EVA_SYNC_RUN_STATUS.COMPLETED_WITH_WARNINGS]: 'orange',
  [EVA_SYNC_RUN_STATUS.COMPLETED_WITH_ERRORS]: 'orange',
  [EVA_SYNC_RUN_STATUS.FAILED]: 'red',
};

type SyncRunsTableProps = {
  churchId: string;
};

const formatRunDate = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MMM DD, YYYY');
};

const getRunSortTimestamp = (run: EvaSyncRunListing): number => {
  const dateValue = run.startedAt ?? run.createdDate;
  return dayjs(dateValue).valueOf();
};

const SyncRunsTable = ({ churchId }: SyncRunsTableProps) => {
  const { data: runs = [], isLoading, isFetching } = useEvaSyncRuns(churchId);
  const [downloadingRunId, setDownloadingRunId] = useState<string | null>(null);

  const sortedRuns = useMemo(
    () => [...runs].sort((runA, runB) => getRunSortTimestamp(runB) - getRunSortTimestamp(runA)),
    [runs]
  );

  const handleDownloadReport = useCallback(
    async (runId: string) => {
      setDownloadingRunId(runId);
      try {
        const blob = await churchesService.getEvaSyncRunReport(churchId, runId);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `eva-sync-report-${runId}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to download Eva sync report:', error);
      } finally {
        setDownloadingRunId(null);
      }
    },
    [churchId]
  );

  const skeletonRows = Array.from({ length: LAYOUT.SKELETON_ROWS }).map((_, index) => (
    <Table.Tr key={`skeleton-${index}`}>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="90%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="50%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="40%" radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="60%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.reportCell}>
        <Group justify="center">
          <Skeleton height={LAYOUT.ACTION_ICON_SIZE} width={LAYOUT.ACTION_ICON_SIZE} radius="sm" />
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const renderStatusBadge = (run: EvaSyncRunListing) => (
    <Badge color={EVA_SYNC_RUN_STATUS_COLORS[run.status]} variant="light">
      {EVA_SYNC_RUN_STATUS_LABELS[run.status]}
    </Badge>
  );

  const renderReportAction = (run: EvaSyncRunListing) => {
    if (!run.hasReport) {
      return null;
    }

    return (
      <Group gap="xs" justify="center">
        <ActionIcon
          aria-label={`Download Eva sync report for ${run.evaPublicId}`}
          variant="subtle"
          loading={downloadingRunId === run.id}
          onClick={() => handleDownloadReport(run.id)}
        >
          <Icon name="IconDownload" size={LAYOUT.ACTION_ICON_SIZE} />
        </ActionIcon>
      </Group>
    );
  };

  const rows = sortedRuns.map((run) => (
    <Table.Tr key={run.id}>
      <Table.Td className={styles.evaPublicIdCell}>
        <Text size="sm" ff="monospace">
          {run.evaPublicId}
        </Text>
      </Table.Td>
      <Table.Td>{EVA_SYNC_BULLETIN_MODE_LABELS[run.bulletinMode]}</Table.Td>
      <Table.Td>{renderStatusBadge(run)}</Table.Td>
      <Table.Td>{formatRunDate(run.startedAt ?? run.createdDate)}</Table.Td>
      <Table.Td className={styles.reportCell}>{renderReportAction(run)}</Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={3}>Eva Sync Runs</Title>
        {isFetching && !isLoading && <Loader size="sm" aria-label="Refreshing sync runs" />}
      </Group>
      <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>EVA Public ID</Table.Th>
            <Table.Th>Bulletin Mode</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Started</Table.Th>
            <Table.Th className={styles.reportCell}>Report</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? skeletonRows : rows}
          {!isLoading && sortedRuns.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed">No Eva sync runs found</Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default SyncRunsTable;
